import React, { useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";

import _ from "lodash";
import {
  ResponseDueIcon,
  PageHeader,
  PageRoot,
  TypeaheadSelect,
  Select
} from "../../components";
import routes from "../../constants/routes";
import formatDate from "../../utils/formatDate";
import { RegulatoryInstrument } from "../../constants/interfaces";
import { connect } from "react-redux";

import RxTable, {
  Table,
  IColumn,
  OnActionChanged,
  TableActionTypes,
  SearchBar,
  CustomWidget,
  FilterBar
} from "../../components/RxTable";

import { useFetch } from "../../utils/useApi";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    padding: "2em"
  },
  toolBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end"
  },
  select: {
    marginBottom: theme.spacing(1),
    width: "50%"
  },
  fullWidthSelect: {
    marginBottom: theme.spacing(1),
    width: "100%"
  }
}));

type Props = {
  history: any;
  instrumentTypes: any;
};

const ActiveRiList: React.FC<Props> = props => {
  const classes = useStyles();

  const { data, loading } = useFetch<RegulatoryInstrument[]>(
    "/ri/getUserRIsForCDImportExport"
  );

  const activeRis = data || [];

  const columns = useMemo(() => {
    const columns: IColumn[] = [
      {
        id: "legalEntity",
        label: "Legal Entity",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <TypeaheadSelect
            className={classes.fullWidthSelect}
            id="legalEntity"
            key="legalEntity"
            placeholder="Legal entity"
            label="Legal Entity"
            onChange={e =>
              onFilterChange({
                value: e ? e.value : "",
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            suggestions={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "premises",
        label: "Premises",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <TypeaheadSelect
            className={classes.fullWidthSelect}
            id="premises"
            key="premises"
            placeholder="Premises"
            label="Premises"
            onChange={e =>
              onFilterChange({
                value: e ? e.value : "",
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            suggestions={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "riType",
        label: "RI Type",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={classes.select}
            id="riType"
            key="riType"
            label="RI Type"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        ),
        parse: cell => props.instrumentTypes[cell]
      },

      {
        id: "riNumber",
        label: "RI Number",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={classes.select}
            id="riNumber"
            key="riNumber"
            label="RI Number"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "riStatus",
        label: "RI Status",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={classes.fullWidthSelect}
            id="riStatus"
            key="riStatus"
            label="RI Status"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "ieAnticipatedImportExportDate",
        type: "date",
        label: "Anticipated Import/Export Date",
        searchable: true,
        parse: formatDate.condensed,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={classes.fullWidthSelect}
            id="ieAnticipatedImportExportDate"
            key="ieAnticipatedImportExportDate"
            label="Anticipated Import/Export Date"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  formatDate.condensed(fValue) === formatDate.condensed(cellValue)
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        ),
        renderCell: dueDate =>
          dueDate ? <ResponseDueIcon dueDate={dueDate} /> : ""
      },
      {
        id: "expiryDate",
        type: "date",
        label: "Expiry Date",
        searchable: true,
        parse: formatDate.condensed,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={classes.fullWidthSelect}
            id="expiryDate"
            key="expiryDate"
            label="Expiry Date"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  formatDate.condensed(fValue) === formatDate.condensed(cellValue)
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        ),
        renderCell: dueDate =>
          dueDate ? <ResponseDueIcon dueDate={dueDate} /> : ""
      }
    ];
    return columns;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageRoot>
      <PageHeader
        label={routes.IMPORT_EXPORT_LIST.label}
        breadcrumbLinks={[
          {
            label: routes.HOME.label,
            route: routes.HOME.url
          }
        ]}
      />
      <RxTable>
        <div className={classes.toolBar}>
          <SearchBar />
          <CustomWidget
            compare={(next, prev) =>
              next.rawData === prev.rawData && next.filters === prev.filters
            }
          >
            {({ dispatch, filters, columns, rawData }) => (
              <FilterBar
                filters={filters}
                dispatch={dispatch}
                data={rawData}
                columns={columns}
              />
            )}
          </CustomWidget>
        </div>

        <Table
          columns={columns}
          loading={loading}
          sorting={{ sortDirection: "desc", id: "expiryDate" }}
          data={activeRis}
          rowsPerPage={25}
          getRowId={row => row.cmAcmCaseIdentifier}
        />

        <OnActionChanged
          actionFilter={action => action.type === TableActionTypes.onRowClick}
        >
          {({ action }) => {
            const rowId = _.get(action.payload, "row.cmAcmCaseIdentifier");

            if (!rowId) throw new Error(`RI id is not availabel`);

            props.history.push(
              `${routes.IMPORT_EXPORT_NOTIFICATION.url}${rowId}`
            );
          }}
        </OnActionChanged>
      </RxTable>
    </PageRoot>
  );
};

const mapStateToProps = ({ regulatoryInstrument }: any) => {
  return {
    instrumentTypes: regulatoryInstrument.instrumentTypes
  };
};

export default connect(mapStateToProps)(ActiveRiList);
